import React from "react";
import { FiBarChart2, FiCompass, FiFileText, FiFlag, FiLayers, FiMessageSquare, FiSearch, FiShield, FiTarget, FiUsers } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuTarget,
  LuSearch,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const uxResearchHeroData = {
  backgroundImage: "/images/serviceBanners/UXResearch.webp",
  whiteImage: "/images/serviceBanners/UXResearchW.webp",
  subTitle: "Creating User Experiences Backed by Research",
  title: (
    <>
      Modern <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">UX Research</span> & Strategy Solutions
    </>
  ),
  description:
    "We design user focused digital experiences through in depth UX research, strategic planning, and design systems that improve usability, enhance user satisfaction, and drive measurable business growth through data driven insights.",
  primaryButton: {
    title: "Schedule Consultation",
    onClick: () => console.log("Consultation clicked"),
  },
  secondaryButton: {
    title: "View Case Studies",
    onClick: () => console.log("Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "User Research & Insights",
      desc: "Research-driven strategies built to improve user experience and engagement.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Scalable UX Frameworks",
      desc: "Flexible UX systems designed for long-term product growth.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Usability & Accessibility",
      desc: "User-focused experiences built for clarity, accessibility, and performance.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Experience Optimization",
      desc: "Modern UX strategies designed to improve usability and conversions.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg", // Re-use generic icon or specific Figma/UX icon
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};

// 2. Stats Block
export const uxResearchStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const uxResearchSolutionsAndProcessData = {
  buildSubtitle: "OUR SERVICES",
  buildTitle: "Custom UX Solutions for Every User Experience Need",
  buildCards: [
    {
      title: "User Journey Research",
      desc: "Research user behavior to improve usability and digital experiences.",
      icon: FiUsers,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise UX Strategy",
      desc: "Scalable UX systems designed for complex business platforms.",
      icon: FiSearch,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Experience Design",
      desc: "Optimize shopping journeys for smoother user interactions.",
      icon: FiLayers,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Service Experience Design",
      desc: "Build seamless digital experiences for booking and service platforms.",
      icon: FiFlag,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech UX Solutions",
      desc: "Design secure and intuitive financial user experiences.",
      icon: FiLayers, // Re-using thematic icon
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom UX Solutions",
      desc: "Tailored UX strategies aligned with business and user needs.",
      icon: FiUsers, // Re-using thematic icon
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR PROCESS",
  processTitle: "Our UX Research & Strategy Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Research",
      description: "Understand user behavior, business goals, and usability challenges.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define user flows, experience goals, and research priorities.",
      icon: LuSearch,
    },
    {
      step: "03",
      title: "UX Design & Prototyping",
      description: "Create intuitive interfaces and testable user experiences.",
      icon: LuPalette,
    },
    {
      step: "04",
      title: "Usability Testing",
      description: "Evaluate interactions and identify experience improvements.",
      icon: LuRocket,
    },
    {
      step: "05",
      title: "Experience Optimization",
      description: "Refine user journeys based on research and feedback.",
      icon: LuPalette,
    },
    {
      step: "06",
      title: "Continuous UX Improvement",
      description: "Improve usability through ongoing testing and insights.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR UX RESEARCH & STRATEGY SERVICES?",
  benefitsTitle: "Why Businesses Invest in UX Research & Strategy",
  benefits: [
    "Research-driven user experience strategies",
    "Improved usability and user engagement",
    "Modern and intuitive interface experiences",
    "Smoother user journeys across digital platforms",
    "Data-backed UX decision making",
    "Scalable experience frameworks",
    "User-focused strategies for better conversions",
  ],
  logoImage: "/images/serviceBanners/UXResearch.webp",
  whiteLogoImage: "/images/serviceBanners/UXResearchW.webp",
};
// 4. Portfolio Data
export const uxResearchPortfolioData = {
  subtitle: "OUR CASE STUDIES",
  title: "UX Strategy for Growth",
  buttonText: "View All Projects",
  buttonLink: "#",
  apps: [
    {
      title: "SaaS Audit",
      badge: "UX Strategy",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Identifying funnel leaks and redefining the user journey for a B2B SaaS dashboard.",
      downloads: "+25% Activation",
      rating: "5.0",
      image: "/images/uiux/uxResearchStrategy/User-Stakeholder-Interviews.png",
    },
  ],
};

// 5. Technologies Data
export const uxResearchTechnologiesData = {
  techTitle: "OUR RESEARCH TOOL STACK",
  technologies: [
    { name: "Figma", icon: "MUI" },
    { name: "Maze/Hotjar", icon: "RestAPI" },
    { name: "Optimal Workshop", icon: "Provider" },
    { name: "UserTesting", icon: "ReactNative" },
    { name: "Miro", icon: "Styled" },
  ],
  trustTitle: "TRUSTED BY PRODUCT TEAMS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 6. About Overview
export const uxResearchAboutData = [
  {
    title: "UX Research Services Focused on Better Digital Experiences",
    content: (
      <>
        Businesses often struggle with poor engagement, confusing navigation, and low conversion rates when digital experiences are not built around real user behavior. Effective{" "}
        <b className="text-text-primary font-semibold">ux research services</b> help organizations understand how users interact with products, identify friction points, and
        improve overall usability. Modern digital experiences require a balance between business goals and customer expectations to remain competitive. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, user research is approached through practical insights that help businesses create smoother
        experiences designed for long-term growth and stronger customer satisfaction.
      </>
    ),
  },
  {
    title: "User Experience Consulting for Smarter Product Decisions",
    content: (
      <>
        Successful digital products are shaped by understanding what users actually need rather than relying on assumptions. Reliable{" "}
        <b className="text-text-primary font-semibold">user experience consulting</b> helps businesses improve product usability, reduce confusion, and support more intuitive
        customer journeys. Companies investing in <b className="text-text-primary font-semibold">ux strategy services</b> often discover opportunities to improve engagement through
        better interaction flows and simplified digital experiences. Tycho Technologies focuses on creating structured experience strategies that support product clarity while
        helping businesses make more informed digital decisions.
      </>
    ),
  },
  {
    title: "UX Strategy Services That Improve User Retention",
    content: (
      <>
        A strong digital experience encourages users to stay engaged and complete important actions without frustration. Businesses working with a trusted{" "}
        <b className="text-text-primary font-semibold">user research company</b> often improve retention because products become easier to navigate and understand. Organizations
        looking to strengthen product usability frequently align research efforts with{" "}
        <a href="https://tychotechnologies.com/ui-ux-design-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          ui ux design services
        </a>{" "}
        to create consistent experiences that support both functionality and visual clarity across digital platforms.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a UX Research Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">ux research company in india</b> continues to grow as businesses prioritize customer satisfaction
        and digital performance. Companies investing in <b className="text-text-primary font-semibold">ux consulting services india</b> often seek scalable research methods that
        improve usability while supporting business objectives. Businesses working with Tycho Technologies gain access to tailored research frameworks designed around real user
        behavior and measurable improvements. Many organizations also improve digital product planning through a trusted{" "}
        <a href="https://tychotechnologies.com/wireframing-prototyping-india" className="text-cyan-400 underline hover:text-cyan-300">
          ui ux prototyping company
        </a>{" "}
        to validate ideas before full-scale development.
      </>
    ),
  },
];

// 7. FAQs (Preserved)
export const uxResearchFaqs = [
  {
    q: "Why should we invest in UX research before UI design?",
    a: "UX research prevents expensive rework. It validates user needs and ensures every design decision supports real user behavior and business objectives.",
  },
  {
    q: "What deliverables will we receive?",
    a: "A complete strategy package including user insights, personas/JTBD, journey maps, IA recommendations, and a prioritized UX roadmap.",
  },
];
